import type {
  HubConnection} from '@microsoft/signalr';
import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';

import {
  ClientEvent,
  HUB_ROUTE,
  ServerMethod,
  type AssistantAudioChunkUpdate,
  type AssistantReplyDto,
  type AssistantSpeechChunkDto,
  type AssistantSpeechStartedDto,
  type AssistantTurnResult,
  type AvatarPoseUpdate,
  type CommitStrokeRequest,
  type ConnectionDto,
  type ExportDto,
  type CreateConnectionRequest,
  type UpdateConnectionRequest,
  type CreateNodeRequest,
  type LabelAudioChunkUpdate,
  type LabelCaptureResult,
  type LockResult,
  type NodeDto,
  type NodeTransformUpdate,
  type PresenceDto,
  type RoomDto,
  type RoomHubEvents,
  type DiagramShownResult,
  type RoomJoinResult,
  type StrokeDto,
  type StrokePointUpdate,
  type TransformDto,
  type UpdateNodeRequest,
  type VoiceChunkUpdate,
} from './wire';

/**
 * Typed wrapper over the room hub.
 *
 * The only write path into a room: locks are enforced against live connection state and
 * changes have to reach the room group in order, so REST stays read-only.
 *
 * Mutations use `invoke` and surface server refusals as rejected promises. The two
 * streaming methods use `send` — fire-and-forget, because a pose that has to wait for an
 * acknowledgement is already stale, and there is a newer one coming.
 */
export class RoomHub {
  private readonly connection: HubConnection;
  private readonly handlers: Partial<RoomHubEvents> = {};

  private roomId: string | null = null;

  /**
   * The role this connection joined with, remembered alongside `roomId` so an automatic
   * reconnect can rejoin as the same thing rather than silently promoting a spectator to a
   * participant. See `join`'s own remarks.
   */
  private asSpectator = false;

  /** This connection's server-assigned identity, known once joined. */
  peerId: string | null = null;

  /** Called after an automatic reconnect resyncs, so the view can rebuild from truth. */
  onResynced: ((result: RoomJoinResult) => void) | null = null;

  /**
   * Called when an automatic reconnect's rejoin is refused — the room is gone, or this
   * caller's org membership was revoked mid-session (`plan/tenancy-resolution.md`), both of
   * which `JoinRoom` now throws a clean `HubException` for. Without this, the rejection above
   * `onreconnected`'s own `await` would become an unhandled promise rejection: `onResynced`
   * would never fire, yet `onConnectionStateChanged` had already reported `Connected`, so the
   * room state and view would keep rendering a room this connection is no longer in.
   *
   * Deliberately left unwired as of this fix (`src/client-web/src/net/` is this change's whole
   * scope) — `main.ts` is where a caller would decide what "the resync failed" means for the
   * UI (drop back to the entry screen, show a fatal error, etc.), and that decision belongs to
   * a separate change. Wiring nothing here is silent-by-omission, which is worse than the
   * unhandled rejection it replaces if this callback is forgotten — so treat "this stays
   * `null`" as a known gap, not a design choice, until something sets it.
   */
  onResyncFailed: ((error: unknown) => void) | null = null;

  onConnectionStateChanged: ((state: HubConnectionState) => void) | null = null;

  constructor(baseUrl = '') {
    // JSON only. MessagePack would halve pose traffic but writes PascalCase keys with no
    // naming policy available, so it cannot share this contract without a second casing
    // convention — see the note in the API's Program.cs.
    this.connection = new HubConnectionBuilder()
      .withUrl(`${baseUrl}${HUB_ROUTE}`)
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    this.registerServerEvents();

    this.connection.onreconnecting(() => this.reportState());
    this.connection.onclose(() => this.reportState());

    // A reconnect gets a fresh connection, which means a fresh PeerId and no group
    // membership. Rejoining is the resync: the snapshot replaces local state wholesale.
    //
    // THE PROMISE THIS RETURNS IS LOAD-BEARING, WHICH IS WHY THE RULE IS SILENCED RATHER THAN
    // SATISFIED. `onreconnected` declares `(connectionId?: string) => void`, so SignalR discards
    // whatever comes back and `no-misused-promises` is right that a rejection would go nowhere.
    // It cannot reject: the whole body below is one try/catch ending at `onResyncFailed`.
    //
    // What the returned promise is actually for is the test suite. `roomHub.test.ts` drives
    // reconnection by calling this captured handler directly and awaiting it — three tests do,
    // and `surfaces a failed resync…` asserts on the promise itself. Rewriting this as
    // `() => void this.resync()` was tried and reverted: it turns that test into "You must
    // provide a Promise to expect()" and leaves the other two passing only because
    // `await undefined` happens to yield exactly one microtask, which is enough for the mocked
    // invoke to settle today and is a guarantee of nothing. That is the shape CLAUDE.md's
    // testing philosophy warns about, so the sequencing stays explicit.
    //
    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- see above: cannot reject, and the suite awaits it
    this.connection.onreconnected(async () => {
      this.reportState();

      if (this.roomId === null) {
        return;
      }

      // `join` reassigns both `this.roomId` and `this.asSpectator` from its own arguments
      // once the invoke resolves, so capturing them here first and passing them back in is
      // what makes this an honest replay rather than a fresh, hardcoded participant join.
      const roomId = this.roomId;
      const asSpectator = this.asSpectator;

      try {
        const result = await this.join(roomId, asSpectator);
        this.onResynced?.(result);
      } catch (error) {
        // Deliberately caught, not left to become an unhandled promise rejection: a rejoin
        // refusal (room gone, or org membership revoked mid-session) must not leave
        // `onConnectionStateChanged` reporting `Connected` while nothing tells the caller the
        // resync itself failed. See `onResyncFailed`'s own remarks for what is, and is not,
        // wired to this yet.
        this.onResyncFailed?.(error);
      }
    });
  }

  get state(): HubConnectionState {
    return this.connection.state;
  }

  /** Registers a handler for a server-to-client event. Safe to call before connecting. */
  on<K extends keyof RoomHubEvents>(event: K, handler: RoomHubEvents[K]): void {
    this.handlers[event] = handler;
  }

  async start(): Promise<void> {
    await this.connection.start();
    this.reportState();
  }

  async stop(): Promise<void> {
    this.roomId = null;
    await this.connection.stop();
  }

  /**
   * Joins a room. No name is sent: the server reads it off the session cookie's
   * principal, because a name a client asserts is a name a client can pick.
   *
   * `asSpectator` defaults to `false` for a caller that does not care, but it is always sent
   * explicitly: SignalR's hub dispatcher binds an invocation's arguments strictly by count
   * against `JoinRoom`'s own parameter count, so an invocation carrying only `roomId` is
   * refused outright rather than falling back to the C# default the way an ordinary call
   * would (see `IRoomHubServer.JoinRoom`'s own remarks). `main.ts` does assert it explicitly
   * today (`hub.join(roomId, isSpectator)`, `isSpectator = choice.mode === 'desktop'`) — a
   * previous version of this comment claimed "nothing in this client asserts it yet", which
   * was stale by the time an automatic reconnect started silently rejoining every spectator
   * as a full participant (see `onreconnected`'s own remarks): the role is real client state,
   * not a placeholder, and has to be remembered and replayed like `roomId` already is.
   */
  async join(roomId: string, asSpectator = false): Promise<RoomJoinResult> {
    const result = await this.connection.invoke<RoomJoinResult>(ServerMethod.JoinRoom, roomId, asSpectator);

    // Remembered so an automatic reconnect can rejoin without the caller's help, and — the
    // role especially — rejoin *as the same thing*, not silently as an ordinary participant.
    this.roomId = roomId;
    this.asSpectator = asSpectator;
    this.peerId = result.peerId;

    return result;
  }

  leave(): Promise<void> {
    if (this.roomId === null) {
      return Promise.resolve();
    }

    const roomId = this.roomId;
    this.roomId = null;

    return this.connection.invoke(ServerMethod.LeaveRoom, roomId);
  }

  /**
   * Points the room at a different diagram, or at none, without anybody leaving it (SPEC-161).
   *
   * **Not a re-join, and must never be implemented as one.** The whole value of opening a diagram
   * in place is that the party survives it — voice does not break, presence does not flicker, and
   * nobody sees anyone else disappear and come back (SPEC-064). Tearing the connection down and
   * rejoining would produce the same nodes and destroy exactly the thing this exists for.
   *
   * Everybody else in the room is told through `diagramShown`; the caller gets the same state as
   * this promise's value, which is the broadcast convention every other state-returning method on
   * this hub already follows.
   */
  showDiagram(diagramId: string | null): Promise<DiagramShownResult> {
    return this.connection.invoke<DiagramShownResult>(
      ServerMethod.ShowDiagram,
      this.requireRoom(),
      diagramId,
    );
  }

  createNode(request: CreateNodeRequest): Promise<NodeDto> {
    return this.connection.invoke<NodeDto>(ServerMethod.CreateNode, this.requireRoom(), request);
  }

  /** Requires this connection to hold the node's lock. */
  updateNode(request: UpdateNodeRequest): Promise<NodeDto> {
    return this.connection.invoke<NodeDto>(ServerMethod.UpdateNode, this.requireRoom(), request);
  }

  /** Requires this connection to hold the node's lock. */
  deleteNode(nodeId: string): Promise<void> {
    return this.connection.invoke(ServerMethod.DeleteNode, this.requireRoom(), nodeId);
  }

  createConnection(request: CreateConnectionRequest): Promise<ConnectionDto> {
    return this.connection.invoke<ConnectionDto>(
      ServerMethod.CreateConnection,
      this.requireRoom(),
      request,
    );
  }

  /** Label, style or reversal. Requires no lock — an edge is not something a hand is on. */
  updateConnection(request: UpdateConnectionRequest): Promise<ConnectionDto> {
    return this.connection.invoke<ConnectionDto>(
      ServerMethod.UpdateConnection,
      this.requireRoom(),
      request,
    );
  }

  deleteConnection(connectionId: string): Promise<void> {
    return this.connection.invoke(ServerMethod.DeleteConnection, this.requireRoom(), connectionId);
  }

  /**
   * Commits a finished stroke. Requires no lock — a stroke does not pre-exist before this
   * call, the same reason createNode/createConnection need none. Resolves with the
   * server's simplified result, which the caller has to apply: this is not also
   * broadcast back, the same trap CreateNode and CreateConnection are shaped to avoid —
   * see plan/whiteboard.md, "CommitStroke returns the simplified result, and the caller
   * has to apply it."
   */
  commitStroke(request: CommitStrokeRequest): Promise<StrokeDto> {
    return this.connection.invoke<StrokeDto>(
      ServerMethod.CommitStroke,
      this.requireRoom(),
      request,
    );
  }

  /**
   * Whole-stroke delete. Unlike deleteNode/deleteConnection, a stale or already-deleted
   * id is a genuine server-side no-op rather than a refusal — an eraser sweep samples
   * contact every frame and can legitimately name the same stroke twice within a few
   * milliseconds. See plan/whiteboard.md, "Erasing."
   */
  deleteStroke(strokeId: string): Promise<void> {
    return this.connection.invoke(ServerMethod.DeleteStroke, this.requireRoom(), strokeId);
  }

  /**
   * Deliberate grab. Resolves with a refusal rather than throwing when someone else holds
   * the node — contention is expected, and the caller needs the holder to render it.
   */
  acquireNodeLock(nodeId: string): Promise<LockResult> {
    return this.connection.invoke<LockResult>(
      ServerMethod.AcquireNodeLock,
      this.requireRoom(),
      nodeId,
    );
  }

  /** Commits the final pose — the one durable write per grab. */
  releaseNodeLock(nodeId: string, finalTransform: TransformDto): Promise<void> {
    return this.connection.invoke(
      ServerMethod.ReleaseNodeLock,
      this.requireRoom(),
      nodeId,
      finalTransform,
    );
  }

  /** Fire-and-forget. Dropped server-side unless this connection holds the lock. */
  streamNodeTransform(update: NodeTransformUpdate): void {
    this.sendIfConnected(ServerMethod.StreamNodeTransform, update);
  }

  /** Fire-and-forget. */
  streamAvatarPose(update: AvatarPoseUpdate): void {
    this.sendIfConnected(ServerMethod.StreamAvatarPose, update);
  }

  /**
   * Fire-and-forget. Never persisted — the durable write happens once, in commitStroke,
   * from the full point list the client committed there, not from anything sent here.
   */
  streamStrokePoint(update: StrokePointUpdate): void {
    this.sendIfConnected(ServerMethod.StreamStrokePoint, update);
  }

  /**
   * Fire-and-forget. Declares the container/codec this connection's next voice stream
   * will use, for every peer to see before the first chunk of it arrives — plan/ai-in-
   * room.md, "Player-to-player voice". Unlike beginAssistantTurn there is no refusal to
   * apply: any number of peers may have a stream open at once.
   */
  startVoiceStream(mimeType: string): void {
    this.sendIfConnected(ServerMethod.StartVoiceStream, mimeType);
  }

  /** Fire-and-forget. Never persisted, the same rule every other stream on this hub follows. */
  streamVoiceChunk(update: VoiceChunkUpdate): void {
    this.sendIfConnected(ServerMethod.StreamVoiceChunk, update);
  }

  /**
   * Fire-and-forget. Safe to call even if no stream is currently open — the server keeps
   * no per-connection state to check against, so this is never refused.
   */
  endVoiceStream(): void {
    this.sendIfConnected(ServerMethod.EndVoiceStream);
  }

  /**
   * Opens the room's one assistant channel. Resolves with a refusal rather than throwing
   * when someone else's turn is already open or no assistant is configured — the same
   * "contention is expected" shape `acquireNodeLock` already uses. Granted, the caller has
   * to apply the "channel open" state locally from this return value — this does not also
   * arrive as `assistantTurnStarted`, which only reaches *other* peers, the same trap every
   * other grant-shaped hub method already requires reading past (CLAUDE.md, "A hub method
   * that returns state does not broadcast it back to you").
   */
  beginAssistantTurn(mimeType: string): Promise<AssistantTurnResult> {
    return this.connection.invoke<AssistantTurnResult>(
      ServerMethod.BeginAssistantTurn,
      this.requireRoom(),
      mimeType,
    );
  }

  /** Fire-and-forget. Dropped server-side unless this connection owns the room's open turn. */
  streamAssistantAudioChunk(update: AssistantAudioChunkUpdate): void {
    this.sendIfConnected(ServerMethod.StreamAssistantAudioChunk, update);
  }

  /**
   * Closes this connection's own open turn. Resolves once the request is acknowledged, not
   * once the assistant has answered — the reply itself is a later, separate
   * `assistantReply` (or `assistantTurnFailed`) broadcast to the whole room, the asker
   * included, because the pipeline runs in the background on the server for exactly this
   * reason (see `RoomSyncHub.EndAssistantTurn`'s own remarks).
   */
  endAssistantTurn(): Promise<void> {
    return this.connection.invoke(ServerMethod.EndAssistantTurn, this.requireRoom());
  }

  /** Deletes the room's saved conversation turns. Nothing renders this yet. */
  clearAssistantHistory(): Promise<void> {
    return this.connection.invoke(ServerMethod.ClearAssistantHistory, this.requireRoom());
  }

  /**
   * Sets the Room's accent colour — skirting and picture rail, not the walls. Resolves
   * with the server's own `RoomDto`, which the caller has to apply: this does not also
   * arrive as `roomUpdated`, the same "notify others, return to caller" trap every other
   * state-returning hub method already requires reading past.
   */
  updateRoomAccent(accentColor: string): Promise<RoomDto> {
    return this.connection.invoke<RoomDto>(
      ServerMethod.UpdateRoomAccent,
      this.requireRoom(),
      accentColor,
    );
  }

  /**
   * Snapshots the whiteboard's current strokes into a durable export and resolves with its
   * metadata, or `null` for a board with no strokes — see `RoomSyncHub.ExportWhiteboard`'s
   * own remarks. No `roomUpdated`-style broadcast to read past: an export changes nothing
   * about the room's own rendered state, so nothing else in the scene needs to react to it.
   */
  exportWhiteboard(): Promise<ExportDto | null> {
    return this.connection.invoke<ExportDto | null>(
      ServerMethod.ExportWhiteboard,
      this.requireRoom(),
    );
  }

  /**
   * Snapshots the room's current nodes and connections into a durable export as Mermaid
   * flowchart source, and resolves with its metadata, or `null` for a room with no nodes
   * and no connections — see `RoomSyncHub.ExportMermaid`'s own remarks. Same "no
   * `roomUpdated`-style broadcast to read past" shape as `exportWhiteboard`.
   */
  exportMermaid(): Promise<ExportDto | null> {
    return this.connection.invoke<ExportDto | null>(
      ServerMethod.ExportMermaid,
      this.requireRoom(),
    );
  }

  /**
   * Opens this connection's own label-capture channel for `nodeId` — the node held when a
   * dominant-hand face button is pressed to speak its label. Resolves with a refusal rather
   * than throwing, the same "contention is expected" shape `beginAssistantTurn` already
   * uses: no assistant configured (labelling reuses the same Gemini endpoint), or this
   * connection is already capturing a label for a different node.
   */
  beginLabelCapture(nodeId: string, mimeType: string): Promise<LabelCaptureResult> {
    return this.connection.invoke<LabelCaptureResult>(
      ServerMethod.BeginLabelCapture,
      this.requireRoom(),
      nodeId,
      mimeType,
    );
  }

  /** Fire-and-forget. Dropped server-side unless this connection owns an open label capture. */
  streamLabelAudioChunk(update: LabelAudioChunkUpdate): void {
    this.sendIfConnected(ServerMethod.StreamLabelAudioChunk, update);
  }

  /**
   * Closes this connection's own open label capture. Resolves once the request is
   * acknowledged, not once transcription has finished — the applied label itself arrives
   * later, as an ordinary `nodeUpdated` broadcast, or a `labelCaptureFailed` if nothing
   * could be applied; see `RoomSyncHub.EndLabelCapture`'s own remarks.
   */
  endLabelCapture(): Promise<void> {
    return this.connection.invoke(ServerMethod.EndLabelCapture, this.requireRoom());
  }

  /**
   * Fire-and-forget. Announces this connection's current authoring mode and which hand holds
   * its prop, so every other peer's avatar can show the right prop in the right hand
   * (plan/remote-tool-visibility.md). No `roomId` argument, the same shape every other
   * fire-and-forget streaming method here already has (`streamAvatarPose`, `startVoiceStream`)
   * — the server resolves the room from this connection's own session, not from anything sent
   * here. `mode`/`hand` should be `ToolModes`/`Hands` members (`wire.ts`); the server refuses
   * anything else outright rather than silently defaulting it.
   */
  switchTool(mode: string, hand: string): void {
    this.sendIfConnected(ServerMethod.SwitchTool, mode, hand);
  }

  // Variadic rather than a single `payload` — endVoiceStream() above takes no argument at
  // all, and passing `undefined` as a stand-in would send it as a real (if empty) argument
  // SignalR then has to bind against a zero-parameter C# method.
  private sendIfConnected(method: string, ...payload: unknown[]): void {
    if (this.connection.state !== HubConnectionState.Connected) {
      return;
    }

    // Nothing to do about a dropped pose but skip it: a newer one is moments away, and an
    // unhandled rejection per frame would be worse than the gap.
    void this.connection.send(method, ...payload).catch(() => {});
  }

  private requireRoom(): string {
    if (this.roomId === null) {
      throw new Error('Join a room before mutating it.');
    }

    return this.roomId;
  }

  private reportState(): void {
    this.onConnectionStateChanged?.(this.connection.state);
  }

  /**
   * One subscription per event, dispatching to whatever handler is registered at the time.
   * Wiring these once in the constructor means handlers can be swapped, or attached late,
   * without touching the connection.
   */
  private registerServerEvents(): void {
    this.connection.on(ClientEvent.PeerJoined, (peer: PresenceDto) =>
      this.handlers.peerJoined?.(peer),
    );
    this.connection.on(ClientEvent.PeerLeft, (peerId: string) => this.handlers.peerLeft?.(peerId));

    this.connection.on(ClientEvent.NodeCreated, (node: NodeDto, byPeerId: string) =>
      this.handlers.nodeCreated?.(node, byPeerId),
    );
    this.connection.on(ClientEvent.NodeUpdated, (node: NodeDto, byPeerId: string) =>
      this.handlers.nodeUpdated?.(node, byPeerId),
    );
    this.connection.on(ClientEvent.NodeDeleted, (nodeId: string, byPeerId: string) =>
      this.handlers.nodeDeleted?.(nodeId, byPeerId),
    );

    this.connection.on(ClientEvent.ConnectionUpdated, (edge: ConnectionDto, byPeerId: string) =>
      this.handlers.connectionUpdated?.(edge, byPeerId),
    );

    this.connection.on(ClientEvent.ConnectionCreated, (edge: ConnectionDto, byPeerId: string) =>
      this.handlers.connectionCreated?.(edge, byPeerId),
    );
    this.connection.on(ClientEvent.ConnectionDeleted, (edgeId: string, byPeerId: string) =>
      this.handlers.connectionDeleted?.(edgeId, byPeerId),
    );

    this.connection.on(ClientEvent.StrokeCreated, (stroke: StrokeDto, byPeerId: string) =>
      this.handlers.strokeCreated?.(stroke, byPeerId),
    );
    this.connection.on(ClientEvent.StrokeDeleted, (strokeId: string, byPeerId: string) =>
      this.handlers.strokeDeleted?.(strokeId, byPeerId),
    );

    this.connection.on(ClientEvent.NodeLockAcquired, (nodeId: string, byPeerId: string) =>
      this.handlers.nodeLockAcquired?.(nodeId, byPeerId),
    );
    this.connection.on(
      ClientEvent.NodeLockReleased,
      (nodeId: string, finalTransform: TransformDto, newVersion: number) =>
        this.handlers.nodeLockReleased?.(nodeId, finalTransform, newVersion),
    );

    this.connection.on(ClientEvent.NodeTransform, (update: NodeTransformUpdate) =>
      this.handlers.nodeTransform?.(update),
    );
    this.connection.on(ClientEvent.AvatarPose, (update: AvatarPoseUpdate) =>
      this.handlers.avatarPose?.(update),
    );
    this.connection.on(ClientEvent.StrokePoint, (update: StrokePointUpdate) =>
      this.handlers.strokePoint?.(update),
    );

    this.connection.on(ClientEvent.VoiceStreamStarted, (peerId: string, mimeType: string) =>
      this.handlers.voiceStreamStarted?.(peerId, mimeType),
    );
    this.connection.on(ClientEvent.VoiceChunk, (update: VoiceChunkUpdate) =>
      this.handlers.voiceChunk?.(update),
    );
    this.connection.on(ClientEvent.VoiceStreamEnded, (peerId: string) =>
      this.handlers.voiceStreamEnded?.(peerId),
    );

    this.connection.on(ClientEvent.AssistantTurnStarted, (byPeerId: string) =>
      this.handlers.assistantTurnStarted?.(byPeerId),
    );
    this.connection.on(ClientEvent.AssistantReply, (reply: AssistantReplyDto) =>
      this.handlers.assistantReply?.(reply),
    );
    this.connection.on(ClientEvent.AssistantSpeechStarted, (started: AssistantSpeechStartedDto) =>
      this.handlers.assistantSpeechStarted?.(started),
    );
    this.connection.on(ClientEvent.AssistantSpeechChunk, (chunk: AssistantSpeechChunkDto) =>
      this.handlers.assistantSpeechChunk?.(chunk),
    );
    this.connection.on(ClientEvent.AssistantSpeechEnded, (utteranceIndex: number) =>
      this.handlers.assistantSpeechEnded?.(utteranceIndex),
    );
    this.connection.on(ClientEvent.AssistantTurnFailed, (reason: string) =>
      this.handlers.assistantTurnFailed?.(reason),
    );
    this.connection.on(ClientEvent.AssistantHistoryCleared, () =>
      this.handlers.assistantHistoryCleared?.(),
    );

    this.connection.on(ClientEvent.RoomUpdated, (room: RoomDto, byPeerId: string) =>
      this.handlers.roomUpdated?.(room, byPeerId),
    );

    this.connection.on(ClientEvent.LabelCaptureFailed, (nodeId: string, reason: string) =>
      this.handlers.labelCaptureFailed?.(nodeId, reason),
    );

    this.connection.on(ClientEvent.ToolSwitched, (peerId: string, mode: string, hand: string) =>
      this.handlers.toolSwitched?.(peerId, mode, hand),
    );
  }
}
